import type { RefObject } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ConciergeTileAsset } from '../../../propValues.types';
import { useIsMobileOrTablet, useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { GalleryTile } from '@apps/card/components/GalleryTile';
import { Box, Flex } from '@withjoy/joykit';
import { BOX_SHADOW_60_02, BOX_SHADOW_8 } from '@apps/card/constants/boxShadow';
import { animationTransition } from '@shared/utils/animationTransition';
import { useVimeoPlayerContext } from '@apps/registry/common/components/Catalog/utils/vimeoPlayer';
import type Player from '@vimeo/player';
import { maybeApplyCallback } from '@shared/utils/maybeApplyCallback';
import type { CardEventType } from '../GalleryItem';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useGalleryConciergeProducts } from '../../../useGalleryConciergeProducts';
import { usePauseOffScreen } from './utils/vimeo/usePauseOffScreen';
import { useAutoplayValues } from '../hooks/useAutoplayValues';
import { tileRowGap, galleryDesktopAspect, galleryMobileAspect } from '@apps/card/components/CardsConfig';
import type { ConciergeServiceCategory } from '@apps/card/concierge/conciergeConfig';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsUserIdle } from '@shared/hooks/useIsUserIdle';

type CardTileConciergeProps = {
  data: ConciergeTileAsset & { type: 'concierge' };
  onSelect: (event: CardEventType) => void;
  category: ConciergeServiceCategory;
};

/**
 * Wrapper around a Vimeo player iframe.
 *
 * When `cover` is true the iframe is sized relative to the container
 * so the video fills it edge-to-edge regardless of aspect-ratio
 * differences (like `object-fit: cover` for an iframe). The parent
 * must set `overflow: hidden` for this to clip the excess.
 */
export const VimeoContainer = ({ r, src, onLoad, cover }: { r?: RefObject<HTMLIFrameElement>; src: string; onLoad?: () => void; cover?: boolean }) => {
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current && r?.current) {
      hasLoaded.current = true;
      onLoad?.();
    }
  }, [onLoad, r, hasLoaded]);

  /*
   * Cover strategy: stretch the iframe beyond the container in both
   * dimensions and center it horizontally, anchoring to the top.
   * The parent must set overflow:hidden + position:relative.
   * Using percentage-based width/height (relative to the positioned
   * parent) so it scales with the container rather than using a
   * fixed transform scale factor.
   */
  return (
    <Box
      ref={r}
      src={src}
      pointerEvents="none"
      border="none"
      outline="none"
      position="absolute"
      top={0}
      left={cover ? '50%' : 0}
      width={cover ? '120%' : '100%'}
      height={cover ? '120%' : '100%'}
      style={cover ? { transform: 'translateX(-50%)' } : undefined}
      title="d"
      loading="lazy"
      as="iframe"
    ></Box>
  );
};

/**
 * The tile's preview frame, defined once (flag on). The tile's own inline frame is a new component
 * on every render, so each re-render remounts the iframe and leaves the player on the old one.
 */
type PreviewFrameProps = { aspectRatio: string; children: React.ReactNode };

const StablePreviewFrame = ({ aspectRatio, children }: PreviewFrameProps) => (
  <Flex
    aspectRatio={aspectRatio}
    overflow="hidden" // Needed for Safari
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
  >
    {children}
  </Flex>
);

export const CardTileConcierge = (props: CardTileConciergeProps) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const isMobile = useIsMobileScreen();
  const { data, category, onSelect } = props;
  const { conciergeProducts } = useGalleryConciergeProducts();
  const { shortName, imageUrl, videoThumbnailUrl, videoUrl: videoUrlProp, index: sortIndex } = data;

  const { VimeoPlayer, prepareForUse } = useVimeoPlayerContext();
  const placeholderImageRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLIFrameElement>(null);
  const [videoPlayer, setVideoPlayer] = useState<Player | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasSeenVideo, setHasSeenVideo] = useState(false);

  const { buttonInteracted } = useCardTelemetry();
  const { conciergeAutoplay: usePlayOnView } = useAutoplayValues();

  const videoUrl = useMemo(() => {
    if (!videoUrlProp || videoUrlProp.trim() === '') return null;

    const url = new URL(videoUrlProp);
    const searchParams = url.searchParams;
    searchParams.append('muted', '1');
    searchParams.append('loop', '1');
    searchParams.append('background', '1');
    return url.href;
  }, [videoUrlProp]);

  const [isOptimized, isFlagLoading] = useIsFeatureEnabled('vimeoPlaybackOptimizationEnabled');
  const isUserIdle = useIsUserIdle(isOptimized);

  const [videoContainerRef, isInView] = usePauseOffScreen(
    () => {
      if (videoPlayer && hasSeenVideo) {
        showAndPlay();
      } else {
        setHasSeenVideo(true);
      }
    },
    () => {
      placeholderImageRef.current?.style.setProperty('opacity', '1');
      if (videoPlayer && hasSeenVideo) {
        videoPlayer.pause();
        videoPlayer.setCurrentTime(0);
      }
    },
    !usePlayOnView,
  );

  const showAndPlay = (player?: Player) => {
    // `usePauseOffScreen` calls this on every render while in view, so it must not start while idle.
    if (isUserIdle) return;
    const video = player ?? videoPlayer;
    if ((hasInteracted || hasSeenVideo) && video?.getPaused()) {
      video.play().catch(() => {
        // Ignore the error because it's expected when the video is forcibly paused
      });
      placeholderImageRef.current?.style.setProperty('opacity', '0');
    }
  };

  const onPlayerLoad = () => {
    if (playerRef && playerRef.current && VimeoPlayer && !videoPlayer) {
      const player = new VimeoPlayer(playerRef.current);
      setVideoPlayer(player);
      showAndPlay(player);
    }
  };

  useEffect(() => {
    prepareForUse();
  }, [prepareForUse]);

  // Keeps the frame on idle, unlike leaving the screen. Resumes only in play-on-view mode; hover mode replays on the next hover.
  useEffect(() => {
    if (!videoPlayer) return;
    if (isUserIdle) {
      videoPlayer.pause().catch(() => undefined);
    } else if (isOptimized && usePlayOnView && isInView && hasSeenVideo) {
      showAndPlay();
    }
    // Only idle transitions drive this; the other values are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserIdle]);

  const aspectRatio = isMobileOrTablet ? '8 / 10' : '9 / 10';
  const AssetPreviewFrame = ({ children }: { children: React.ReactNode }) => {
    return (
      <Flex
        aspectRatio={aspectRatio}
        overflow="hidden" // Needed for Safari
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        {children}
      </Flex>
    );
  };
  const PreviewFrame: React.FC<PreviewFrameProps> = isOptimized ? StablePreviewFrame : AssetPreviewFrame;

  const onMouseOver = () => {
    if (videoPlayer && hasInteracted) {
      showAndPlay();
    } else if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  const onMouseExit = () => {
    placeholderImageRef.current?.style.setProperty('opacity', '1');
    if (videoPlayer && hasInteracted) {
      videoPlayer.pause();
      videoPlayer.setCurrentTime(0);
    }
  };

  return (
    <GalleryTile
      alignContent="start"
      rowGap={tileRowGap}
      aspectRatio={isMobile ? galleryMobileAspect : galleryDesktopAspect}
      onClick={() => {
        buttonInteracted({
          name: 'paperlustConciergeService_gallery_tile',
          suite: data.longName,
          cost: data.costPerHundredString,
          sortIndex,
          orderOfItems: conciergeProducts[category].map(product => product.longName),
        });
        onSelect({ id: data.id, type: 'conciergeselectedevent' });
        onMouseExit();
      }}
    >
      <div ref={videoContainerRef}>
        <PreviewFrame aspectRatio={aspectRatio}>
          <Box
            position="relative"
            width="100%"
          >
            <Box
              onMouseOver={maybeApplyCallback(!isMobileOrTablet && !usePlayOnView, onMouseOver)}
              onMouseOut={maybeApplyCallback(!isMobileOrTablet && !usePlayOnView, onMouseExit)}
              borderRadius={1}
              boxShadow={BOX_SHADOW_8}
              width="100%"
              height="100%"
              overflow="hidden"
              position="relative"
              transition={animationTransition('box-shadow')}
              _groupHover={{
                boxShadow: `${BOX_SHADOW_8},${BOX_SHADOW_60_02}`,
              }}
            >
              {/* Not while the flag loads: resolving on swaps the frame type, which would remount the iframe under the player. */}
              {!isFlagLoading && ((!usePlayOnView && !isMobileOrTablet && hasInteracted) || (usePlayOnView && hasSeenVideo)) && (
                <Box
                  position="relative"
                  paddingTop="177.78%"
                  backgroundColor="mono1"
                  backgroundPosition="center"
                  backgroundSize="cover"
                  backgroundRepeat="no-repeat"
                  style={{
                    backgroundImage: `url('${videoThumbnailUrl}')`,
                  }}
                >
                  {videoUrl && (
                    <VimeoContainer
                      onLoad={onPlayerLoad}
                      r={playerRef}
                      src={videoUrl}
                    />
                  )}
                </Box>
              )}
              <Box
                ref={placeholderImageRef}
                position="absolute"
                transition={animationTransition('opacity')}
                top={0}
                left={0}
                backgroundImage={`url('${imageUrl}')`}
                backgroundPosition="center"
                backgroundSize={'cover'}
                size="100%"
              />
            </Box>
          </Box>
        </PreviewFrame>
      </div>
      <GalleryTile.Content title={shortName} />
    </GalleryTile>
  );
};
